import { ISearchExtraBaseFilter } from '@models/search/ISearchExtraFilters';
import { ISearchRequest } from '@models/search/search.types';

const isEmptyValue = (value: unknown) => value === undefined || value === null || value === '';

/** Стабильная сериализация: ключи по алфавиту, пустые значения выброшены. */
const stableEntries = (source: Record<string, unknown> | undefined): [string, unknown][] =>
    Object.entries(source ?? {})
        .filter(([, value]) => !isEmptyValue(value))
        .sort(([a], [b]) => a.localeCompare(b));

/**
 * Подпись применённого фильтра документов. Одинаковый по смыслу фильтр даёт одинаковую
 * подпись независимо от порядка ключей и от «пустых» полей формы — иначе каждое
 * открытие панели считалось бы новым запросом.
 */
export const buildFilterSignature = (filter: ISearchExtraBaseFilter | undefined): string => {
    if (!filter) {
        return '';
    }

    const baseRequisites = stableEntries(filter.baseRequisites as Record<string, unknown> | undefined).map(
        ([key, value]) => `${key}=${String(value)}`,
    );

    const flow = filter.flowsFields?.selectedFlow?.key ?? '';
    const fields = (filter.flowsFields?.fieldsValues ?? [])
        .filter((field) => !isEmptyValue(field.value) || !isEmptyValue(field.valueTo))
        .map((field) => `${field.key}=${String(field.value ?? '')}~${String(field.valueTo ?? '')}`)
        .sort();

    const parts = [...baseRequisites, flow ? `flow:${flow}` : '', ...fields].filter(Boolean);
    return parts.join('|');
};

const orderPart = (request: ISearchRequest) =>
    request.orderBy ? `${request.orderBy.propName}.${request.orderBy.asc ? 'asc' : 'desc'}` : '';

const filterPart = (request: ISearchRequest) =>
    request.scope === 'docs' ? buildFilterSignature(request.filter) : '';

/**
 * Ключ запроса: результат в сторе принимается только если ключ совпадает с текущим.
 * Включает всё, что влияет на набор строк, — область, текст, режим, страницу,
 * размер, сортировку и фильтр (для документов).
 */
export const buildSearchQueryKey = (request: ISearchRequest): string =>
    [
        request.scope,
        request.mode,
        request.text.trim(),
        `p${request.pageIndex}`,
        `s${request.pageSize}`,
        orderPart(request),
        filterPart(request),
    ].join(' ');

/**
 * Часть ключа без страницы: по ней живёт `totalCount` — он не меняется при
 * перелистывании, поэтому запрашивается один раз на набор.
 */
export const buildResultSetKey = (request: ISearchRequest): string =>
    [request.scope, request.mode, request.text.trim(), orderPart(request), filterPart(request)].join(' ');
